import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  Input,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import { FormGroup, FormBuilder, Validators } from '@angular/forms';
import { ActivatedRoute, Router, NavigationEnd } from '@angular/router';

import * as _ from 'lodash';

// Animations
import { slideInDownAnimation } from '../../app/app.animation';

// Common
import {
  AngularMaterialService,
  ErrorParseService,
  LGEDataService,
  IExternalData,
  IPending,
} from '../../commons';

// State
import { Store } from '@ngrx/store';
import * as fromExternalManagement from './state/external-users-management.selectors';
import * as ExternalManagement from './state/external-users-management.actions';
import * as app from '../../app/state/app.actions';
import * as lgeApp from '../../app/state/app.actions';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { Observable, skipWhile, Subscription } from 'rxjs';

@Component({
  selector: 'external-users-management',
  templateUrl: 'external-users-management.component.html',
  styleUrls: ['external-users-management.scss'],
  animations: [slideInDownAnimation],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ExternalUsersManagementScreenComponent
  implements OnInit, OnDestroy, AfterViewInit
{
  @ViewChild(MatPaginator) paginator: MatPaginator;
  @ViewChild(MatSort) sort: MatSort;

  @Input() private pending$: Observable<IPending>;
  private pendingSubscription: Subscription;
  private pending: boolean;
  private pendingMessage: string = '';
  @Input() private error$: Observable<string | null>;
  private errorSubscription: Subscription;
  private error: string | null;
  @Input() private stateData$: Observable<IExternalData[] | null>;
  stateDataSubscription: Subscription;
  pendingApprovalData: IExternalData[] | null;
  approvedRejectedData: IExternalData[] | null;
  sortDirection = { Ascending: 'asc', Descending: 'desc' };

  public dataSource = new MatTableDataSource();
  public externalUserAccessCols = [
    'OrganizationName',
    'EIN',
    'FirstName',
    'LastName',
    'Phone',
    'Email',
    'Status',
    'EINStatus',
    'Approve',
    'Reject',
  ];

  constructor(
    private store: Store<fromExternalManagement.State>,
    private ref: ChangeDetectorRef,
    private fb: FormBuilder,
    private ams: AngularMaterialService,
    private errorParse: ErrorParseService,
    private _dataService: LGEDataService,
    private route: ActivatedRoute,
    private router: Router
  ) {
    this.router.routeReuseStrategy.shouldReuseRoute = function () {
      return false;
    };

    this.router.events.subscribe((evt) => {
      if (evt instanceof NavigationEnd) {
        this.router.navigated = false;
      }
    });
  }

  ngAfterViewInit() {
    this.dataSource.paginator = this.paginator;
  }

  // Opens sidenav if on mobile
  public openSidenav(): void {
    this.store.dispatch(new lgeApp.OpenSidenav());
  }

  ngOnInit() {
    this.selectState();
    this.startSubscriptions();
    this.getExternalUserApplicants();
    this.dataSource.filterPredicate = (data: IExternalData, filter: string) =>
      data.externalUser.accessStatus == filter;
    this.dataSource.sortingDataAccessor = (item: IExternalData, property) => {
      switch (property) {
        case 'OrganizationName':
          return item.orgInfoExt.name.toLocaleLowerCase();
        case 'FirstName':
          return item.externalUser.firstName.toLocaleLowerCase();
        case 'LastName':
          return item.externalUser.lastName.toLocaleLowerCase();
        case 'Email':
          return item.externalUser.email.toLocaleLowerCase();
        case 'Status':
          return item.externalUser.accessStatus.toLocaleLowerCase();
        default:
          return item.orgInfoExt.name.toLocaleLowerCase();
      }
    };
    this.dataSource.sort = this.sort;
  }

  // Select state
  selectState(): void {
    this.pending$ = this.store.select(
      fromExternalManagement.getExternalManagementScreenPending
    );
    this.stateData$ = this.store
      .select(fromExternalManagement.getExternalManagementState)
      .pipe(
        skipWhile((userData) => userData === undefined || userData === null)
      );
    this.error$ = this.store
      .select(fromExternalManagement.getExternalManagementScreenErrorMessage)
      .pipe(
        skipWhile(
          (error) => error === undefined || error === null || error === ''
        )
      );
  }

  displayLoader(pending: boolean): void {
    if (pending) {
      setTimeout(() => {
        this.ams.showLoader(this.pendingMessage);
        this.ref.markForCheck();
      });
    } else {
      this.ams.closeDialog().then(() => {
        if (
          this.error !== null &&
          this.error !== undefined &&
          this.error !== ''
        ) {
          this.displayError(this.error);
        } 
        // if(this.pendingMessage !== ' ') {
        //   this.ams.showAlert('Success', this.pendingMessage);
        // }
      });
    }
  }

  displayError(error: any): void {
    let errorMessage;
    if (this.errorParse.foundKeyWord(error, 'CORPCOMMONWS')) {
      errorMessage = this.errorParse.translateCCError(error);
    } else {
      errorMessage = error instanceof Object ? error.message : error;
    }
    if (errorMessage === 'approved' || errorMessage === 'rejected')
      this.ams.showAlert('Success', `Successfully ${errorMessage}`);
    else this.ams.showAlert('Loading Error', errorMessage);
    this.ams.alert.afterClosed().subscribe((result) => {
      this.store.dispatch(
        new ExternalManagement.ExternalManagementScreenErrorReset()
      );
    });
  }

  // Subscribe to state data and mark it for changes
  getExternalUserApplicants() {
    this.store.dispatch(new ExternalManagement.LoadExternalManagementState());
  }

  startSubscriptions(): void {
    this.pendingSubscription = this.pending$.subscribe((data: IPending) => {
      this.pending = data.pending;
      this.pendingMessage = data.pendingMessage;
      this.displayLoader(this.pending);
      this.ref.markForCheck();
    });
    this.errorSubscription = this.error$.subscribe((data) => {
      this.error = data;
      this.ref.markForCheck();
    });
    this.stateDataSubscription = this.stateData$.subscribe((externalUsers) => {
      if (externalUsers) {
        this.pendingApprovalData = _.filter(
          externalUsers,
          (userData) => userData.externalUser.accessStatus == 'pending'
        );
        this.approvedRejectedData = _.orderBy(
          _.filter(
            externalUsers,
            (userData) => userData.externalUser.accessStatus != 'pending'
          ),
          ['externalUser.externalUserId', 'externalUser.accessStatus'],
          ['desc', 'desc']
        );
        this.ref.markForCheck();
      }
    });
  }

  // Unsubscribe from state data changes
  endSubscriptions(): void {
    this.pendingSubscription.unsubscribe();
    this.errorSubscription.unsubscribe();
    this.stateDataSubscription.unsubscribe();
  }

  ngOnDestroy() {
    this.endSubscriptions();
  }
}
